import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { testSetService } from '../services/testSetService';
import { TestSet, Question } from '../types/models';
import { Button } from '../components/common/Button';
import { useAuth } from '../context/AuthContext';
import { attemptService } from '../services/attemptService';
import './TestSolverPage.css';

type UserAnswers = Record<string, Array<'A' | 'B' | 'C'>>;

export const TestSolverPage = () => {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [testSet, setTestSet] = useState<TestSet | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<UserAnswers>({});
  const [isFinished, setIsFinished] = useState(false);
  const [score, setScore] = useState(0);
  const [finishedAttemptId, setFinishedAttemptId] = useState<string | null>(null);

  useEffect(() => {
    if (!testId) return;
    const fetchTest = async () => {
      const fetchedTest = await testSetService.getTestSetById(testId);
      if (fetchedTest) {
        setTestSet(fetchedTest);
      } else {
        navigate('/kursant');
      }
      setIsLoading(false);
    };
    fetchTest();
  }, [testId, navigate]);

  const handleAnswerSelect = (questionId: string, answer: 'A' | 'B' | 'C') => {
    const currentAnswers = userAnswers[questionId] || [];
    const question = testSet?.questions.find(q => q.id === questionId);
    const isSingleChoice = question ? question.correctOptions.length === 1 : false;

    let newAnswers: Array<'A' | 'B' | 'C'>;

    if (isSingleChoice) {
      newAnswers = [answer];
    } else {
      newAnswers = currentAnswers.includes(answer)
        ? currentAnswers.filter(a => a !== answer)
        : [...currentAnswers, answer];
    }
    
    setUserAnswers(prev => ({ ...prev, [questionId]: newAnswers }));
  };

  const checkAnswerCorrectness = (question: Question, answers: Array<'A' | 'B' | 'C'>) => {
    if (!answers || answers.length === 0) return false;
    const sortedUserAnswers = [...answers].sort();
    const sortedCorrectOptions = [...question.correctOptions].sort();
    
    return sortedUserAnswers.length === sortedCorrectOptions.length && 
           sortedUserAnswers.every((value, index) => value === sortedCorrectOptions[index]);
  };
  
  const handleFinishTest = async () => {
    if (!testSet || !user) return;

    let correctAnswersCount = 0;
    testSet.questions.forEach(q => {
      if (checkAnswerCorrectness(q, userAnswers[q.id])) {
        correctAnswersCount++;
      }
    });
    setScore(correctAnswersCount);

    try {
      const newAttempt = await attemptService.saveAttempt({
        testSetId: testSet.id,
        testSetName: testSet.name,
        score: correctAnswersCount,
        totalQuestions: testSet.questions.length,
        userAnswers: userAnswers,
      }, user.id);
      setFinishedAttemptId(newAttempt.id);
    } catch (error) {
      console.error("Nie udało się zapisać wyniku testu", error);
    } finally {
      setIsFinished(true);
    }
  };

  if (isLoading) return <div>Ładowanie testu...</div>;
  if (!testSet) return <div>Nie znaleziono testu.</div>;

  const currentQuestion = testSet.questions[currentQuestionIndex];
  
  if (isFinished) {
    return (
      <div className="solver-container results-container">
        <h1>Wynik testu: {testSet.name}</h1>
        <p className="score">Twój wynik: {score} / {testSet.questions.length}</p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
          <Button onClick={() => navigate('/kursant')}>Wróć do panelu</Button>
          {finishedAttemptId && (
            <Link to={`/przeglad/${finishedAttemptId}`} className="btn btn-secondary">
              Zobacz odpowiedzi
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="solver-container">
      <h2>{testSet.name}</h2>
      <p>Pytanie {currentQuestionIndex + 1} z {testSet.questions.length}</p>
      
      <div className="question-card">
        {currentQuestion.imageUrl && 
            <img src={currentQuestion.imageUrl} alt="Ilustracja do pytania" className="solver-question-image"/>}
        <h3>{currentQuestion.text}</h3>
        
        {currentQuestion.correctOptions.length > 1 && 
            <p className="multiple-choice-info">To pytanie ma więcej niż jedną poprawną odpowiedź.</p>}
            
        <div className="options">
          {['A', 'B', 'C'].map(option => {
            const optionKey = `option${option}` as keyof Question;
            const isChecked = userAnswers[currentQuestion.id]?.includes(option as 'A' | 'B' | 'C');
            const choiceType = currentQuestion.correctOptions.length > 1 ? 'checkbox' : 'radio';

            return (
              <label key={option} className={`option-label ${isChecked ? 'selected' : ''}`}>
                <input
                  type={choiceType}
                  name={currentQuestion.id}
                  checked={isChecked || false}
                  onChange={() => handleAnswerSelect(currentQuestion.id, option as 'A' | 'B' | 'C')}
                />
                <span>{option}.</span> {String(currentQuestion[optionKey])}
              </label>
            )
          })}
        </div>
      </div>

      <div className="navigation-buttons">
        <Button 
          onClick={() => setCurrentQuestionIndex(prev => prev - 1)}
          disabled={currentQuestionIndex === 0}
          variant="secondary"
        >
          Poprzednie
        </Button>
        {currentQuestionIndex < testSet.questions.length - 1 ? (
          <Button onClick={() => setCurrentQuestionIndex(prev => prev + 1)}>
            Następne
          </Button>
        ) : (
          <Button onClick={handleFinishTest}>
            Zakończ Test
          </Button>
        )}
      </div>
    </div>
  );
};